import { QVueGlobals } from 'quasar';
import { defineStore } from 'pinia';
import { useUserStore } from 'src/core/stores/user';
import FarmPlotsIcon from '@/assets/icons/farm-plot-icon.svg?component';
import { NavigationItemGroup } from 'src/core/types/app';
import { useApiStore } from 'src/core/stores/api';
import { useDatasetEnumsStore } from 'src/core/stores/enum/dataset';
import { useParcelEnumsStore } from 'src/core/stores/enum/parcel';
import routes from 'src/router/routes';
import { useUserEnumsStore } from 'src/core/stores/enum/user';
import { useAnalyticsStore } from 'src/core/stores/analytics';
import { useExportEnumsStore } from 'src/core/stores/enum/export';
import { usePermissionStore } from 'src/core/stores/permission';
import { useBillingStore } from 'src/modules/usage/stores/billing';
import { markRaw } from 'vue';

type AppStore = {
  initialized: boolean;
  loading: boolean;
  errorDetails?: {
    message: string;
    type: string;
  };
  navigationItemsGroup: NavigationItemGroup[];
  unAuthorizedlist: string[];
  $q: QVueGlobals | null;
  t: any;
};

/**
 * Represents the application store.
 */
export const useAppStore = defineStore('app', {
  state: () =>
    ({
      initialized: false,
      loading: false,
      navigationItemsGroup: [],
      unAuthorizedlist: [],
      errorDetails: undefined,
      $q: null,
      t: null,
    } as AppStore),

  getters: {
    /**
     * Custom getter for accessing the route.
     * @returns {boolean} The route authorised access.
     */
    isRouteAuthorized: (state) => (routeName: string) => {
      return !state.unAuthorizedlist.includes(routeName);
    },
  },

  actions: {
    /**
     * Initializes the application store.
     * @param $q - The QVueGlobals object.
     */
    async initialize($q: QVueGlobals): Promise<boolean> {
      this.loading = true;
      this.$q = $q;
      const apiStore = useApiStore();
      const userStore = useUserStore();
      const analyticsStore = useAnalyticsStore();
      await analyticsStore.initialize();
      apiStore.createRequestInstance();
      if (userStore.access_token) {
        this.$q.loading.show();
        await userStore.getUserInfo();
        userStore.startConsumptionPolling();
        this.$q.loading.hide();
        await this.getAllEnums();
      }
      await this.initializeNavigationItems();
      await this.fillUnAuthorizedList();
      await analyticsStore.ready();
      this.loading = false;
      this.initialized = true;
      return true;
    },

    /**
     * Fills the unAuthorizedlist array with the names of the routes that are not authorized to be shown or accessed.
     */
    fillUnAuthorizedList() {
      const permissionStore = usePermissionStore();
      const userStore = useUserStore();
      this.unAuthorizedlist = [];
      routes.forEach((route) => {
        if (route.meta?.disabled) {
          this.unAuthorizedlist.push(route.name as string);
        } else if (route.meta?.permission_to_access) {
          if (
            !permissionStore.checkUserAccess(
              route.meta.permission_to_access as string
            )
          ) {
            this.unAuthorizedlist.push(route.name as string);
          }
        } else if (route.meta?.permission_to_access_fnc) {
          if (
            !route.meta?.permission_to_access_fnc(
              userStore.user,
              permissionStore.checkUserAccess
            )
          ) {
            this.unAuthorizedlist.push(route.name as string);
          }
        }
      });
    },

    /**
     * Initializes the navigation items.
     */
    async initializeNavigationItems() {
      // const { t } = useI18n();
      this.navigationItemsGroup = [
        {
          items: [
            {
              name: 'datasets',
              route: { name: 'datasets' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'data_table',
              },
            },
            {
              name: 'results',
              route: { name: 'results' },
              icon: {
                type: 'material-icons-outlined',
                value: 'table_chart',
              },
            },
            {
              name: 'farm plots',
              route: { name: 'farmPlots' },
              icon: {
                type: 'component',
                value: markRaw(FarmPlotsIcon),
              },
            },
            {
              name: 'parcels & DDS',
              route: { name: 'parcels' },
              icon: {
                type: 'material-icons-outlined',
                value: 'inventory_2',
              },
              // badge: 'beta', //t('app.sidebar.beta_badge'),
            },
            {
              name: 'Vault',
              route: { name: 'vault' },
              icon: {
                type: 'material-icons-outlined',
                value: 'lock_open',
              },
              // badge: 'beta', //t('app.sidebar.beta_badge'),
            },
          ],
          position: 'normal',
        },
        {
          name: 'User Management',
          items: [
            {
              name: 'users',
              route: { name: 'userAccess' },
              icon: {
                type: 'material-icons-outlined',
                value: 'person',
              },
            },
            {
              name: 'customers',
              route: { name: 'customers' },
              icon: {
                type: 'material-icons-outlined',
                value: 'apartment',
              },
            },
            {
              name: 'suppliers',
              route: { name: 'suppliers' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'trolley',
              },
            },
          ],
          position: 'normal',
        },
        {
          name: 'Documentation',
          items: [
            {
              name: 'Tests & Resolutions',
              route: { name: 'Tests & Resolutions' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'lab_panel',
              },
            },
          ],
          position: 'normal',
        },
        {
          items: [
            {
              name: 'Usage analytics',
              route: { name: 'usageAnalytics' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'monitoring',
              },
            },
            {
              name: 'Api access',
              route: { name: 'apiAccess' },
              icon: {
                type: 'material-icons',
                value: 'generating_tokens',
              },
            },
            {
              name: 'Diagnostics',
              route: { name: 'diagnostic-v2' },
              icon: {
                type: 'material-icons-outlined',
                value: 'construction',
              },
            },
            {
              name: 'Helpdesk',
              link: 'https://helpdesk.meridiaverify.com/',
              icon: {
                type: 'material-icons-outlined',
                value: 'contact_support',
              },
            },
            {
              name: 'Knowledge base',
              link: 'https://help.meridiaverify.com/en/',
              icon: {
                type: 'material-icons-outlined',
                value: 'school',
              },
            },
          ],
          position: 'bottom',
        },
        // {
        //   name: 'test management',
        //   icon: TestIcon,
        //   items: [
        //     {
        //       name: 'test',
        //       route: { name: 'tests' },
        //       icon: TestIcon,
        //     },
        //     // {
        //     //   name: 'test profile',
        //     //   route: { name: 'testProfile' },
        //     //   icon: GroupIcon,
        //     // },
        //   ],
        //   position: 'normal',
        // },
        // {
        //   items: [
        //     {
        //       name: 'datasources',
        //       route: { name: 'datasources' },
        //       icon: DatasourceIcon,
        //     },
        //   ],
        //   position: 'normal',
        // },
      ];
    },

    /**
     * Retrieves all the enums from different stores.
     * @returns {Promise<void>} A promise that resolves when all the enums are retrieved.
     */
    async getAllEnums() {
      const datasetEnumsStore = useDatasetEnumsStore();
      await datasetEnumsStore.getEnums();
      const userEnumsStore = useUserEnumsStore();
      await userEnumsStore.getEnums();
      const ExportEnumsStore = useExportEnumsStore();
      await ExportEnumsStore.getEnums();
      const parcelEnumsStore = useParcelEnumsStore();
      await parcelEnumsStore.getEnums();
      // await testEnumsStore.getEnums();

      const billingStore = useBillingStore();

      const permissionStore = usePermissionStore();
      if (
        permissionStore.checkUserAccess('app.billing.api:read_pricing_plans')
      ) {
        await billingStore.getBillingPlans();
      }
    },
  },
});
